import { createSelector, select } from '@ngrx/store';
import { FinishedLubesFeatureId, FinishedLubesState, initialFinishedLubesState } from './state';
import { groupBy, meanBy, uniq } from 'lodash-es';
import { ImpactChartData } from '../../interfaces';

const selectFeature = (state: any): FinishedLubesState => {
    return state?.[FinishedLubesFeatureId] ?? initialFinishedLubesState;
};

export const bestMatch = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.bestMatch,
);

export const allForecastItems = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.allForecastItems,
);

export const filteredImpactItems = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.filteredImpactItems,
);
export const impactChartData = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.impactChartData,
);

export const mbuListBase = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.mbuListBase,
);

export const mbuList = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.mbuList,
);

export const pbiList = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.pbiList,
);

export const pbiListBase = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.pbiListBase,
);

export const productTableRows = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.productTableRows,
);

export const outputDriverData = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.outputDriverData,
);

export const selectedPBI = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.selectedPBI,
);

export const selectedMbu = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.selectedMbu,
);

export const isLoading = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.isLoading,
);

export const error = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.error,
);

export const lastUpdatedDate = createSelector(
    selectFeature,
    (state: FinishedLubesState) => {
        const modelCreationDates = uniq(
            state.allForecastItems.map((x) => x.createdAt),
        ).sort();
        return modelCreationDates[0];
    },
);

export const nextRunDate = createSelector(
    lastUpdatedDate,
    (lastUpdatedDate: Date | string | undefined) => {
        if (!lastUpdatedDate) return undefined;

        const date = new Date(lastUpdatedDate);
        if (isNaN(date.getTime())) return undefined;

        const getNthSaturday = (
            year: number,
            month: number,
            n: number,
        ): Date => {
            const firstDay = new Date(year, month, 1);
            const dayOfWeek = firstDay.getDay();
            const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
            const firstSaturday = new Date(year, month, 1 + daysUntilSaturday);
            const nthSaturday = new Date(firstSaturday);
            nthSaturday.setDate(firstSaturday.getDate() + (n - 1) * 7);
            return nthSaturday;
        };

        const currentMonth = date.getMonth();
        const currentYear = date.getFullYear();

        const candidates = [
            getNthSaturday(currentYear, currentMonth, 1), // 1st Saturday of current month
            getNthSaturday(currentYear, currentMonth, 3), // 3rd Saturday of current month
            getNthSaturday(currentYear, currentMonth + 1, 1), // 1st Saturday of next month
            getNthSaturday(currentYear, currentMonth + 1, 3), // 3rd Saturday of next month
        ];

        const validCandidates = candidates.filter((c) => c > date);
        if (validCandidates.length === 0) return undefined;

        return validCandidates.reduce((earliest, current) =>
            current < earliest ? current : earliest,
        );
    },
);

export const historyFLFeedback = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.historicalFeedback.history,
);

export const isHistoryFLFeedbackLoading = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.historicalFeedback.isLoading,
);

export const flFeedbackProdLvl6 = createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.historicalFeedback.prodLvl6,
);

export const selectPvCurveEntry = (key: string) => createSelector(
    selectFeature,
    (state: FinishedLubesState) => state.pvCurveCache[key]
);

export const selectActivePvCurveEntry = createSelector(
    selectFeature,
    (state: FinishedLubesState) =>
        state.selectedPvCurveKey ? state.pvCurveCache[state.selectedPvCurveKey] : undefined
);
